import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Plus, Eye, Clock, CheckCircle, 
  AlertTriangle, Package, DollarSign, Calendar, 
  ArrowLeft, MessageCircle, Download, FileText, 
  Trash2, Edit, MoreVertical, MapPin
} from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabase } from '../lib/supabase';

const MyRFQsPage = () => {
  const navigate = useNavigate();
  const { user } = useSupabase();
  const [rfqs, setRfqs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Check if user is logged in
  useEffect(() => {
    if (!user) {
      navigate('/signin?redirect=my-rfqs');
    }
  }, [user, navigate]);
  
  // Fetch user's RFQs
  useEffect(() => {
    const fetchRFQs = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('rfq_requests')
          .select(`
            *,
            buyer_request_categories (
              category_id,
              categories:category_id (name)
            ),
            supplier_bids (
              id,
              supplier_id,
              price,
              status,
              suppliers:supplier_id (organization)
            )
          `)
          .eq('buyer_id', user.id)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        setRfqs(data || []);
      } catch (err) {
        console.error('Error fetching RFQs:', err);
        setError('Failed to load your RFQs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRFQs();
  }, [user]);
  
  // Filter RFQs based on search and status
  const filteredRFQs = rfqs.filter(rfq => {
    const matchesSearch = rfq.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || rfq.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });
  
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Open
        </span>;
      case 'in_progress':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Clock className="h-3 w-3 mr-1" />
          In Progress
        </span>;
      case 'fulfilled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Fulfilled
        </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Cancelled
        </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Expired
        </span>;
      default:
        return null;
    }
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Check if RFQ is expiring soon (within 3 days)
  const isExpiringSoon = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 3 && diffDays > 0;
  };
  
  // Cancel RFQ
  const handleCancelRFQ = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this RFQ?')) return;
    
    try {
      const { error } = await supabase
        .from('rfq_requests')
        .update({ status: 'cancelled' })
        .eq('id', id)
        .eq('buyer_id', user?.id);
      
      if (error) throw error;
      
      // Update local state
      setRfqs(rfqs.map(rfq => 
        rfq.id === id ? { ...rfq, status: 'cancelled' } : rfq
      ));
      
    } catch (err) {
      console.error('Error cancelling RFQ:', err);
      alert('Failed to cancel RFQ. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="mr-4 text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My RFQs</h1>
                <p className="text-gray-600">Manage your requests for quotation</p>
              </div>
            </div>
            <Link 
              to="/rfq/create" 
              className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>New RFQ</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search your RFQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>

        {/* RFQs List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your RFQs...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading RFQs</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredRFQs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No RFQs Found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || filterStatus !== 'all'
                ? 'No RFQs match your search criteria. Try adjusting your filters.'
                : "You haven't submitted any RFQs yet. Create your first RFQ to get quotes from suppliers."}
            </p>
            <Link 
              to="/rfq/create" 
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors inline-flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Create New RFQ</span>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredRFQs.map((rfq) => (
              <div key={rfq.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div className="mb-4 md:mb-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{rfq.title}</h3>
                        {getStatusBadge(rfq.status)}
                        {isExpiringSoon(rfq.expiry_date) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Expiring Soon
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="inline-flex items-center mr-4">
                          <Package className="h-4 w-4 text-gray-400 mr-1" />
                          {rfq.quantity_required} units
                        </span>
                        <span className="inline-flex items-center mr-4">
                          <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                          Created: {formatDate(rfq.created_at)}
                        </span>
                        <span className="inline-flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-1" />
                          Expires: {formatDate(rfq.expiry_date)}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                        {rfq.shipping_country}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {rfq.bid_count} {rfq.bid_count === 1 ? 'Quote' : 'Quotes'} Received
                      </div>
                      {rfq.budget_min && rfq.budget_max && (
                        <div className="text-sm text-gray-600 mb-2">
                          Budget: ${rfq.budget_min} - ${rfq.budget_max} per unit
                        </div>
                      )}
                      <div className="flex space-x-2">
                        <Link
                          to={`/rfq/${rfq.id}`}
                          className="bg-orange-600 text-white px-3 py-1 rounded text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-1"
                        >
                          <Eye className="h-3 w-3" />
                          <span>View Quotes</span>
                        </Link>
                        {rfq.status === 'open' && (
                          <button
                            onClick={() => handleCancelRFQ(rfq.id)}
                            className="border border-gray-300 text-gray-700 px-3 py-1 rounded text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-1"
                          >
                            <Trash2 className="h-3 w-3" />
                            <span>Cancel</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="text-sm text-gray-700 mb-4 line-clamp-2">
                      {rfq.description}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {rfq.buyer_request_categories?.map((category: any, index: number) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {category.categories?.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {rfq.supplier_bids && rfq.supplier_bids.length > 0 && (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Quotes</h4>
                      <div className="space-y-3">
                        {rfq.supplier_bids.slice(0, 3).map((bid: any) => (
                          <div key={bid.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                              <div className="font-medium text-gray-900">{bid.suppliers?.organization}</div>
                              <div className="text-sm text-gray-600">${bid.price} per unit</div>
                            </div>
                            <div>
                              {bid.status === 'pending' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              )}
                              {bid.status === 'accepted' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Accepted
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                        {rfq.supplier_bids.length > 3 && (
                          <Link 
                            to={`/rfq/${rfq.id}`}
                            className="text-orange-600 hover:text-orange-700 text-sm font-medium flex items-center"
                          >
                            View all {rfq.supplier_bids.length} quotes
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRFQsPage;